const Customer = require('@/lib/Customers/Customer');
const EventNames = require('@/lib/Events/EventNames');
const { cloneDeep } = require('@/functions/clone');
const { container } = require('@/container');
const { containsDeliverableItem } = require('@/libraries/DigiTickets/Cart/cartContents');
const { customerGiftAidValidatonErrors } = require('@/lib/Customers/customerGiftAidValidationFunctions');
const { MODAL_NAMES__EPOS } = require('@/epos/vue/modals/MODAL_NAMES__EPOS');
const { suppressModalCancelledErrorOrReThrow } = require('@/lib/Errors');
const { updateAngularUi } = require('@/functions/angular');

/**
 * @param $filter
 * @param $location
 * @param $modal
 * @param $modalInstance
 * @param $scope
 * @param $timeout
 * @param {CartService} cartService
 * @param {ConnectivityChecker} connectivityChecker
 * @param {Customer} customer
 * @param {CustomerAccountService} customerAccountService
 * @param {DialogService} dialogService
 * @param {{langKey: string, type?: string}|null} errorMsg
 * @param {FieldRepository} fieldRepository
 * @param {ModalFactory} modalFactory
 * @param {boolean} paymentInProgress
 * @param {ToastFactory} toastFactory
 * @param {UserService} userService
 * @param {boolean} validateForGiftAid
 */
const CustomerDetailsCtrl = function CustomerDetailsCtrl(
    $filter,
    $location,
    $modal,
    $modalInstance,
    $scope,
    $timeout,
    cartService,
    connectivityChecker,
    customer,
    customerAccountService,
    dialogService,
    errorMsg,
    fieldRepository,
    modalFactory,
    paymentInProgress,
    toastFactory,
    userService,
    validateForGiftAid
) {
    $scope.isValid = true;
    $scope.connectivityChecker = connectivityChecker;
    $scope.validateForGiftAid = !!validateForGiftAid;

    // Only show the alert about custom fields moving if the company has some fields.
    $scope.showFieldsAlert = false;
    fieldRepository
        .countAllActiveNonAskEarlyFields()
        .then((count) => {
            $scope.showFieldsAlert = count > 0;
            updateAngularUi();
        });

    /**
     * The modal can be triggered from the Sell screen or the Payment screen so there may be a payment in progress.
     * If we have arrived in this modal from the payment modal then disable the customer edit button because although
     * we could edit the address, there is no way to get back to the payment modal at the moment.
     *
     * @type {boolean}
     */
    $scope.paymentInProgress = paymentInProgress;

    $scope.setValid = (isValid) => {
        $scope.isValid = isValid;
    };

    $scope.minQueryLength = 3;

    $scope.search = {
        query: '',
        resultsQuery: '',
        /**
         * @type {CustomerAccount[]|null}
         */
        results: null,
        loading: false,
    };
    $scope.searchScrollerControl = {};

    $scope.isValidSearchQuery = function isValidSearchQuery(query) {
        return query.length >= $scope.minQueryLength;
    };

    /**
     * @param {Address} address
     */
    const setDefaultDeliveryCountry = (address) => {
        if (address.isEmpty()) {
            address.country = userService.currentBranch.country;
        }
    };

    $scope.cart = cartService;
    $scope.cartService = cartService;
    $scope.cartContainsDeliverableItem = containsDeliverableItem($scope.cart.getItems());
    $scope.canEditDeliveryAddress = !cartService.currentCart.isEditingOrder;

    /**
     * We get passed in a clone of the cart's current customer on load. We'll update this one and send it back
     * as the result only if OK is pressed. If the modal is closed without pressing OK the changes to this are lost.
     *
     * @type {Customer}
     */
    $scope.customer = customer;

    /**
     * This will keep a separate instance of the cart's current customer on load in order to keep an eye on
     * changes during the session. It is currently used to track whether memberships should be removed from
     * the cart because the user has changed.
     *
     * @type {Customer}
     */
    $scope.originalCustomer = customer;

    /**
     * Show/hide the address dropdown.
     *
     * @type {boolean}
     */
    $scope.useCustomerAddress = false;

    /**
     * Selected key of customer address dropdown.
     *
     * @type {?number}
     */
    $scope.selectedDeliveryAddressKey = null;

    $scope.user = userService;

    /**
     * @type {{text: string, type: string}|null}
     */
    $scope.errorMsg = null;

    /**
     * @type {{langKey: string, type?: string}|null}
     */
    $scope.setErrorMsg = (newErrorMsg) => {
        $scope.errorMsg = null;

        if (newErrorMsg) {
            $scope.errorMsg = {
                text: $filter('lang')(newErrorMsg.langKey),
                type: newErrorMsg.type ? newErrorMsg.type : 'danger',
            };
        }
    };

    $scope.setErrorMsg(errorMsg);

    const revalidateErrors = () => {
        $scope.setErrorMsg(null);
        if ($scope.validateForGiftAid) {
            if ($scope.customer && $scope.customer.hasData()) {
                const giftAidErrors = customerGiftAidValidatonErrors($scope.customer);
                if (giftAidErrors.length !== 0) {
                    let errors = giftAidErrors.map((e) => $filter('lang')(e));
                    $scope.errorMsg = {
                        text: $filter('lang')('CUSTOMER_DETAILS.GIFT_AID_FIELDS_MISSING', { fields: errors.join(' ') }),
                        type: 'danger',
                    };
                }
            }

            if ($scope.customer && !$scope.customer.hasData()) {
                $scope.errorMsg = {
                    text: $filter('lang')('SELL.GIFT_AID_CUSTOMER_INFO_REQUIRED'),
                    type: 'danger',
                };
            }
        } else {
            if ($scope.customer && !$scope.customer.hasData()) {
                $scope.errorMsg = {
                    text: $filter('lang')('CUSTOMER_DETAILS.REQUIRED_CONTACT_FOR_ACCOUNT'),
                    type: 'danger',
                };
            }
        }
    };

    if ($scope.validateForGiftAid) {
        revalidateErrors();
    }

    /**
     * Close the dialog, and return the changes to the opener.
     */
    $scope.ok = function () {
        $scope.setValid(true);
        $scope.setErrorMsg(null);

        let resultToReturn = {
            customer: $scope.customer,
        };

        // Broadcast to child controllers that ok has been pressed and should run their validation, and add anything
        // they want to return to the result object.
        $scope.$broadcast('customer-details-modal.ok-pressed', resultToReturn);

        $timeout(() => {
            if (!$scope.isValid) {
                return;
            }

            // Remove memberships from the cart if the customer has changed.
            if ($scope.cart.containsMemberships() && $scope.customerHasChanged()) {
                $scope.cart.clearMemberships();
            }

            $modalInstance.close(resultToReturn);
        });
    };

    /**
     * Just close the dialog without returning anything to the opener.
     * (No changes should be saved)
     */
    $scope.cancel = function cancel() {
        $modalInstance.dismiss('cancel');
    };

    // Close the modal on escape.
    $scope.$on(EventNames.ESC_KEY_DOWN, () => $scope.cancel());

    $scope.searchCustomerAccounts = function searchCustomerAccounts() {
        if ($scope.search.loading) {
            return;
        }

        if (!$scope.isValidSearchQuery($scope.search.query)) {
            toastFactory.warningTop('MIN_QUERY_LENGTH_ERROR');
            return;
        }

        $scope.resultsQuery = $scope.search.query;
        $scope.search.results = [];

        $scope.search.loading = true;
        customerAccountService.query(
            {
                q: $scope.search.query,
            },
            function (results) {
                // Sort out which contacts to show in the search results.
                for (let i = 0; i < results.length; i++) {
                    $scope.prepareAccountContactsForList(results[i]);
                }

                $scope.search.results = results;
                $scope.search.loading = false;
            },
            function () {
                $scope.search.results = {};
                $scope.search.loading = false;
            }
        );
    };

    $scope.prepareAccountContactsForList = function prepareAccountContactsForList(account) {
        let listContacts = [];
        let noNameContacts = [];

        for (let i = 0; i < account.contacts.length; i++) {
            if (account.contacts[i].ID === account.primaryContact.ID) {
                // Skip the primary contact because they are shown at the top.
                continue;
            }

            if (account.contacts[i].ref === $scope.search.query) {
                // If the contact's ref is the ref which was searched for, put that contact at the top of the list.
                listContacts.unshift(account.contacts[i]);
            } else if (!account.contacts[i].getFullName()) {
                // Put no names at the end.
                noNameContacts.push(account.contacts[i]);
            } else {
                listContacts.push(account.contacts[i]);
            }
        }

        // Add no name contacts back at the end.
        listContacts = listContacts.concat(noNameContacts);

        let length = listContacts.length;
        if (length <= 4) {
            // Show up to 3 contacts and "+x more" item.
            // If there are 4, there is no point in showing "+1  more". We can just use the space to show the 1 more.
            account.additionalContactsList = listContacts;
            account.additionalContactsOverflow = 0;
        } else {
            account.additionalContactsList = listContacts.splice(0, 3);
            account.additionalContactsOverflow = length - 3;
        }
    };

    $scope.searchCustomerKeydown = function searchCustomerKeydown($event) {
        if ($event.which === 13) {
            $event.preventDefault();
            $event.stopPropagation();
            $scope.searchCustomerAccounts();
        }
    };

    /**
     * Select a customer account from the search results.
     *
     * @param {CustomerAccount} account
     */
    $scope.selectCustomerAccount = function selectCustomerAccount(account) {
        // Clear existing customer data.
        const newCustomer = new Customer();

        // Set account.
        // TODO: Prompt which address / contact to use?
        newCustomer.setAccount(account);

        // Set membership (if any).
        // TODO: Prompt which membership to use?
        let membership = account.getActiveMembership();
        if (membership) {
            newCustomer.setMembership(membership);
        }

        $scope.setCustomer(newCustomer);
        revalidateErrors();
    };

    /**
     * Clears the customer in the cart. Will prompt the user if the cart contains memberships.
     *
     * @returns {void}
     */
    $scope.clearCustomer = async function clearCustomer() {
        if (this.cart.containsMemberships()) {
            this.showClearCustomerDialog().then((confirmed) => {
                if (confirmed) {
                    $timeout(() => {
                        $scope.customer = new Customer();
                    });
                }
            });
        } else {
            $scope.customer = new Customer();
        }

        revalidateErrors();
    };

    /**
     * Opens a dialog prompting the user to confirm they want to remove the customer.
     * Returns the users input as a promise.
     *
     * @returns {Promise<boolean>}
     */
    $scope.showClearCustomerDialog = function showClearCustomerDialog() {
        return dialogService.confirm(
            ['CART.REMOVE_OR_EDIT_CUSTOMER'],
            {
                cancelLabel: 'DIALOGS.NO_LABEL',
                okLabel: 'DIALOGS.YES_LABEL',
                cancelColor: 'danger',
                okColor: 'success',
            }
        );
    };

    /**
     * Checks to see if the customer has changed since the cart has launched.
     * It is currently used to check if memberships should be removed from the cart.
     *
     * @returns {boolean}
     */
    $scope.customerHasChanged = function customerHasChanged() {
        if (
            $scope.customer
            && $scope.customer.account
            && $scope.originalCustomer
            && $scope.originalCustomer.account
        ) {
            return $scope.customer.account.ID !== $scope.originalCustomer.account.ID;
        }
        return false;
    };

    /**
     * Clone delivery address to selected customer address.
     *
     */
    $scope.setDeliveryAddress = function setDeliveryAddress() {
        $scope.customer.deliveryAddress = cloneDeep($scope.customer.account.addresses[this.selectedDeliveryAddressKey]);
    };

    $scope.showEditCustomerForm = function showEditCustomerForm(currentCustomer) {
        if ($scope.paymentInProgress) {
            return null;
        }

        if (currentCustomer.account) {
            // Redirect off to the manage account page
            $location.path('/accounts/' + currentCustomer.account.ID);
            $scope.cancel();
        } else {
            const clonedCustomer = cloneDeep(currentCustomer);
            return $scope.showCustomerForm(clonedCustomer, false);
        }
        return null;
    };

    $scope.showNewCustomerForm = function showNewCustomerForm() {
        let newCustomer = new Customer();
        return $scope.showCustomerForm(newCustomer, true);
    };

    $scope.showCustomerForm = (customerObject, createAccountStartsChecked) => {
        // Determine an error message to display in the customer details modal.
        let initialError = null;
        // If there is an existing error message, and it is not the gift aid one, pass it through.
        if ($scope.errorMsg?.text && $scope.errorMsg.text !== $filter('lang')('SELL.GIFT_AID_CUSTOMER_INFO_REQUIRED')) {
            initialError = new Error($scope.errorMsg.text);
        }

        container.vueModalFactory
            .open(MODAL_NAMES__EPOS.CUSTOMER_DETAILS, {
                customer: customerObject,
                initialError,
                createAccount: createAccountStartsChecked,
                validateForGiftAid: $scope.validateForGiftAid,
            })
            .then((data) => {
                // If 'Create an account for this customer' is ticked then a CustomerAccount object is
                // returned otherwise a Customer object is returned
                if (data.customerAccount) {
                    $scope.customer.setAccount(data.customerAccount);
                } else {
                    $scope.setCustomer(data.customer);
                }

                revalidateErrors();
                updateAngularUi();
            })
            .catch(suppressModalCancelledErrorOrReThrow);
    };

    /**
     * Note: We only want to clear the errors if this method is called after the initial load, otherwise it will reset
     * any existing errors.
     *
     * @param {Customer} newCustomer
     * @param {boolean} clearErrors
     */
    $scope.setCustomer = (newCustomer, clearErrors = true) => {
        $scope.customer = newCustomer;
        setDefaultDeliveryCountry($scope.customer.deliveryAddress);

        if (clearErrors) {
            $scope.setErrorMsg(null);
        }

        if ($scope.cartContainsDeliverableItem && $scope.canEditDeliveryAddress) {
            if ($scope.customer.deliveryContact) {
                // Set the delivery contact to the customer's name if it's not already set.
                if (
                    !$scope.customer.deliveryContact.firstName
                    && !$scope.customer.deliveryContact.lastName
                ) {
                    const contact = $scope.customer.getContact();
                    $scope.customer.deliveryContact.firstName = contact.firstName;
                    $scope.customer.deliveryContact.lastName = contact.lastName;
                }
            } else {
                // Set the delivery contact to the customer's contact if it doesn't exist.
                $scope.customer.deliveryContact = $scope.customer.getContact();
            }
        }
    };

    // Call setCustomer now to initialize things.
    $scope.setCustomer($scope.customer, false);
};

module.exports = CustomerDetailsCtrl;
